import { FolderOwnership } from '@/types/source-folder';
import type { PaginatedResponse } from '@/types/responses';

export const CACHE_KEYS = {
    SYSTEM_OVERVIEW: 'system-overview',
    SYSTEM_VERSION: 'system-version',
    SOURCE_LIST_PAGES: 'source-list-pages',
    SOURCE_PREFIX: 'source-',
    SOURCE_FOLDERS: 'source-folders',
    SOURCE_FOLDER_PAGES: 'source-folder-pages',
    SOURCE_FOLDER_CHILDREN: 'source-folder-children',
    EVENT_PREFIX: 'event-',
    RECENT_EVENTS: 'recent-events',
    RECENT_ALERTS: 'recent-alerts',
    RECENT_DATABASE_EVENTS: 'recent-database-events',
    QUERY_PREFIX: 'query:',
    ALERT_RULE_PREFIX: 'alert-rule-',
    ALERT_RULES_CHECK: 'alert-rules-check',
    ALERT_UNIT_CAPACITY: 'alert-unit-capacity',
    ALERT_RULES_ALL: 'alert-rules-all',
    ALERT_RULES_ALL_PAGES: 'alert-rules-all-pages',
    SOURCES_PREFIX: 'sources',
    AUTH_USER: 'auth-user',
    ADMIN_ORGANIZATION_CONFIGURATION: 'admin-organization-configuration',
    ADMIN_FEATURE_FLAGS: 'admin-feature-flags',
    CAMERAS: 'cameras',
    ALL_VEHICLES: 'all-vehicles',
    ALL_VEHICLES_STATS: 'all-vehicles-stats',
    REPORTS_LIST: 'reports-list',
    REPORT_EVENTS_PREFIX: 'report-events-',
    REPORT_EVENT_PREFIX: 'report-event-',
    REPORT_PIPELINE_PREFIX: 'report-pipeline-',
    EXPORT_JOB_PREFIX: 'export-job-',
    PROCEDURE_TEMPLATES: 'procedure-templates',
    ALL_VEHICLES_TAGS: 'all-vehicles-tags',
    ALL_PERSONS: 'all-persons',
    ALL_PERSONS_STATS: 'all-persons-stats',
    ALL_PERSONS_TAGS: 'all-persons-tags',
    VEHICLE_PREFIX: 'vehicle-',
    EVENT_ENTITIES_PREFIX: 'event-entities-',
    VEHICLE_EVENTS_PREFIX: 'vehicle-events',
    VEHICLE_STATS_PREFIX: 'vehicle-stats',
    PERSON_EVENTS_PREFIX: 'person-events',
    PERSON_STATS_PREFIX: 'person-stats',
    PERSON_OCCURRENCES_PREFIX: 'person-occurrences',
} as const;

export function getSourceCacheKey(sourceId: string): string {
    return `${CACHE_KEYS.SOURCE_PREFIX}${sourceId}`;
}

export function getAlertRuleEventsCacheKey(alertRuleId: string): string {
    return `${CACHE_KEYS.ALERT_RULE_PREFIX}${alertRuleId}-events`;
}

export function getSourceAlertsCacheKey(sourceId: string): string {
    return `${CACHE_KEYS.SOURCE_PREFIX}${sourceId}-alerts`;
}

export function getAlertRuleCacheKey(alertRuleId: string): string {
    return `${CACHE_KEYS.ALERT_RULE_PREFIX}${alertRuleId}`;
}

export function getEventCacheKey(eventId: string): string {
    return `${CACHE_KEYS.EVENT_PREFIX}${eventId}`;
}

export function getQueryCacheKey(params: Record<string, unknown>): string {
    return `${CACHE_KEYS.QUERY_PREFIX}${JSON.stringify(params)}`;
}

export function getSourceEventsCacheKey(
    sourceId: string,
    orderBy?: string,
    classIds?: number[],
): string {
    const orderSuffix = orderBy ? `-${orderBy}` : '';
    const classSuffix = classIds?.length ? `-class:${classIds.join(',')}` : '';
    return `${CACHE_KEYS.SOURCE_PREFIX}${sourceId}-events${orderSuffix}${classSuffix}`;
}

export function getEventEntitiesCacheKey(eventId: string): string {
    return `${CACHE_KEYS.EVENT_ENTITIES_PREFIX}${eventId}`;
}

export function getVehicleCacheKey(vehicleId: string): string {
    return `${CACHE_KEYS.VEHICLE_PREFIX}${vehicleId}`;
}

export function getVehicleEventsCacheKey(
    vehicleId: string,
    limit: number,
): string {
    return `${CACHE_KEYS.VEHICLE_EVENTS_PREFIX}-${vehicleId}-${limit}`;
}

export function getVehicleStatsCacheKey(
    vehicleId: string,
    period: string,
): string {
    return `${CACHE_KEYS.VEHICLE_STATS_PREFIX}-${vehicleId}-${period}`;
}

export function getPersonStatsCacheKey(
    personId: string,
    period: string,
): string {
    return `${CACHE_KEYS.PERSON_STATS_PREFIX}-${personId}-${period}`;
}

export const ALL_FOLDER_OWNERSHIPS = 'all';

export function getSourceFoldersKey(
    excludeEmptyFolders: boolean = false,
    ownership: FolderOwnership | undefined = undefined,
    orderBy: string = '',
    search: string = '',
) {
    return (
        pageIndex: number,
        previousPageData: PaginatedResponse<unknown> | null,
    ): readonly [string, boolean, string, string, string, number] | null => {
        if (
            pageIndex > 0 &&
            previousPageData &&
            previousPageData.page.page >= previousPageData.page.totalPages
        ) {
            return null;
        }
        return [
            CACHE_KEYS.SOURCE_FOLDER_PAGES,
            excludeEmptyFolders,
            ownership ?? ALL_FOLDER_OWNERSHIPS,
            orderBy,
            search,
            pageIndex,
        ] as const;
    };
}

export interface SourceListKeyQuery {
    search: string;
    visibility: string;
    type: string;
    orderBy: string;
}

export function getSourceListKey(query: SourceListKeyQuery, pageSize: number) {
    return (
        pageIndex: number,
        previousPageData: { items: unknown[] } | null,
    ): readonly [string, string, string, string, string, number] | null => {
        if (previousPageData && previousPageData.items.length === 0) {
            return null;
        }
        return [
            CACHE_KEYS.SOURCE_LIST_PAGES,
            query.search,
            query.visibility,
            query.type,
            query.orderBy,
            pageIndex * pageSize,
        ] as const;
    };
}

export function isSourceListPageCacheKey(key: unknown): boolean {
    return Array.isArray(key) && key[0] === CACHE_KEYS.SOURCE_LIST_PAGES;
}

export function isSourceFoldersCacheKey(key: unknown): boolean {
    return Array.isArray(key) && key[0] === CACHE_KEYS.SOURCE_FOLDERS;
}

export function isSourceFoldersPageCacheKey(key: unknown): boolean {
    return Array.isArray(key) && key[0] === CACHE_KEYS.SOURCE_FOLDER_PAGES;
}

export function isSourcesCacheKey(key: unknown): boolean {
    return Array.isArray(key) && key[0] === CACHE_KEYS.SOURCES_PREFIX;
}

export function isReportsListCacheKey(key: unknown): boolean {
    return Array.isArray(key) && key[0] === CACHE_KEYS.REPORTS_LIST;
}

export function getReportEventsCacheKey(
    reportId: string,
    minConfidence: number,
) {
    return [CACHE_KEYS.REPORT_EVENTS_PREFIX, reportId, minConfidence] as const;
}

export function getReportEventCacheKey(eventId: string): string {
    return `${CACHE_KEYS.REPORT_EVENT_PREFIX}${eventId}`;
}

export function getReportPipelineStatusCacheKey(reportId: string): string {
    return `${CACHE_KEYS.REPORT_PIPELINE_PREFIX}${reportId}`;
}

export function getExportJobCacheKey(jobId: string): readonly [string, string] {
    return [CACHE_KEYS.EXPORT_JOB_PREFIX, jobId] as const;
}
